import type { ReactNode } from "react";
import { useMediaQuery } from "usehooks-ts";
import { SkedPostPreview } from "@/components/Posts/SkedPostPreview";
import { cn } from "@/lib/utils";
import { usePostMarkup } from "./PostMarkupProvider";

export type PostReviewTab = "preview" | "activity" | "markup";

export function PostReviewTabs({
  activeTab,
  onChange,
  separateActivity = false,
}: {
  activeTab: PostReviewTab;
  onChange: (tab: PostReviewTab) => void;
  separateActivity?: boolean;
}) {
  const tabs: { value: PostReviewTab; label: string; icon: string }[] = [
    { value: "preview", label: "Preview", icon: "fa-play" },
    { value: "activity", label: "Activity", icon: "fa-comment" },
    { value: "markup", label: "Markup", icon: "fa-comment-pen" },
  ];
  const visibleTabs = tabs.filter(
    (tab) => !separateActivity || tab.value !== "activity"
  );
  return (
    <div
      className={cn(
        "inline-flex h-8 items-center gap-1 rounded-lg bg-[#f4f4f6] p-1 font-inter",
        separateActivity ? "w-[197px]" : "w-[292px]"
      )}
      role="tablist"
      aria-label="Post review"
    >
      {visibleTabs.map((tab, index) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={activeTab === tab.value}
          tabIndex={activeTab === tab.value ? 0 : -1}
          onKeyDown={(event) => {
            const direction =
              event.key === "ArrowRight"
                ? 1
                : event.key === "ArrowLeft"
                  ? -1
                  : 0;
            const nextIndex =
              event.key === "Home"
                ? 0
                : event.key === "End"
                  ? visibleTabs.length - 1
                  : direction
                    ? (index + direction + visibleTabs.length) %
                      visibleTabs.length
                    : null;
            if (nextIndex === null) return;
            event.preventDefault();
            onChange(visibleTabs[nextIndex].value);
            const buttons =
              event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                "[role=tab]"
              );
            buttons?.[nextIndex]?.focus();
          }}
          onClick={() => onChange(tab.value)}
          className={cn(
            "flex h-6 min-w-0 flex-1 items-center justify-center gap-2 rounded-[5px] border-0 px-3 text-xs leading-4",
            activeTab === tab.value
              ? "bg-white font-semibold text-neutral-800 shadow-[0_1px_1px_rgba(16,24,40,0.05)]"
              : "bg-transparent font-medium text-neutral-500 hover:bg-white/60"
          )}
        >
          <i
            className={cn(
              "flex size-[15px] shrink-0 items-center justify-center text-xs",
              activeTab === tab.value
                ? "fa-solid text-blue-500"
                : "fa-regular text-neutral-500",
              tab.icon
            )}
            aria-hidden
          />
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function PostMarkupSurface({
  children,
}: {
  children: (review: ReturnType<typeof usePostMarkup>) => ReactNode;
}) {
  return <>{children(usePostMarkup())}</>;
}

export function PostReviewEditorSidebar({ mediaId }: { mediaId?: string }) {
  const review = usePostMarkup();
  const wide = useMediaQuery("(min-width: 1920px)");
  const tab =
    wide && review.activeTab === "activity" ? "preview" : review.activeTab;
  return (
    <div className="flex h-full min-h-0 shrink-0 border-l border-neutral-100">
      <aside
        className="flex h-full min-h-0 w-[432px] flex-col bg-[#fcfaff]"
        aria-label="Media editor post review"
      >
        <header className="flex h-14 shrink-0 items-center border-b border-neutral-100 bg-[#edeaff] px-4 py-3">
          <PostReviewTabs
            activeTab={tab}
            onChange={review.setActiveTab}
            separateActivity={wide}
          />
        </header>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div
            className={
              tab === "markup" ? "flex min-h-0 flex-1 flex-col" : "hidden"
            }
          >
            {review.renderMarkup(mediaId)}
          </div>
          {tab === "markup" ? null : tab === "activity" ? (
            review.renderActivity("drawer")
          ) : (
            <div className="p-4">
              {review.post && (
                <SkedPostPreview
                  post={review.post as never}
                  env="schedugram-client"
                />
              )}
            </div>
          )}
        </div>
      </aside>
      {wide && (
        <aside
          className="flex h-full min-h-0 w-[432px] flex-col border-l border-neutral-100 bg-white"
          aria-label="Media editor activity"
        >
          <header className="flex h-14 shrink-0 items-center gap-2 border-b border-neutral-100 px-4 text-sm font-medium">
            <i className="fa-regular fa-comment" aria-hidden />
            Activity
          </header>
          <div className="flex min-h-0 flex-1 flex-col">
            {review.renderActivity("drawer")}
          </div>
        </aside>
      )}
    </div>
  );
}
